import * as FiIcons from 'react-icons/fi';
import { addDays, isBefore, differenceInHours } from 'date-fns';

const { FiCheckCircle, FiClock, FiAlertTriangle, FiAlertCircle } = FiIcons;

/**
 * Fristeskaleringsregler (Legal Deadlines according to EU Directive / AML)
 * 
 * Level 0: Normal - Alt i rute
 * Level 1: Nærmer seg frist (Urgent) - < 48t for bekreftelse, < 7 dager for tilbakemelding
 * Level 2: Frist utgått (Overdue) - Frist passert
 * Level 3: Alvorlig forsinket (Critical) - > 7 dager over frist
 */

export const ESCALATION_LEVELS = {
  NORMAL: 0,
  WARNING: 1,
  OVERDUE: 2,
  CRITICAL: 3
};

/**
 * Beregner frister for en sak basert på lovkrav og metodikk.
 */
export const calculateDeadlines = (createdAt) => {
  const createdDate = new Date(createdAt);
  return {
    ack: addDays(createdDate, 7),
    feedback: addDays(createdDate, 90),
  };
};

/**
 * Beregner eskaleringsnivå for kontradiksjon.
 */
export const getContradictionEscalation = (deadline) => {
  if (!deadline) return 'normal';
  
  const now = new Date();
  const deadlineDate = new Date(deadline);
  
  if (isBefore(deadlineDate, now)) {
    return 'critical'; // Fristen er utgått
  }
  
  const hoursLeft = differenceInHours(deadlineDate, now);
  if (hoursLeft <= 48) {
    return 'warning'; // Mindre enn 48 timer igjen
  }
  
  return 'normal';
};

/**
 * Returnerer farge og tekst for eskaleringsstatus (for kontradiksjonspesifikk UI).
 */
export const getEscalationUI = (status) => {
  switch (status) {
    case 'critical':
      return {
        color: 'text-red-600 bg-red-50 border-red-200',
        label: 'Frist utgått - Kritisk oppfølging kreves',
        icon: 'FiAlertCircle'
      };
    case 'warning':
      return {
        color: 'text-amber-600 bg-amber-50 border-amber-200',
        label: 'Nærmer seg frist (under 48t)',
        icon: 'FiClock'
      };
    default:
      return {
        color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
        label: 'Innenfor frist',
        icon: 'FiCheckCircle'
      };
  }
};

export const getDeadlineData = (report) => {
  if (!report) return null;

  const now = new Date();
  const created = new Date(report.created_at || report.createdAt);
  
  // Lovpålagte frister
  const deadlineAck = new Date(report.deadline_ack || new Date(created.getTime() + 7 * 24 * 60 * 60 * 1000));
  const deadlineFeedback = new Date(report.deadline_feedback || new Date(created.getTime() + 90 * 24 * 60 * 60 * 1000));

  // Tilsvar fra omvarslet (normalt 14 dager etter varsling)
  const deadlineReported = report.deadline_reported_reply ? new Date(report.deadline_reported_reply) : null;

  const isAcked = report.is_confirmed || report.isConfirmed || report.status !== 'new';
  const isClosed = ['resolved', 'closed', 'arkivert'].includes(report.status);
  const hasReportedReplied = report.contradiction_status === 'fullført' || (report.history?.some(h => h.action.toLowerCase().includes('tilsvar')));

  let status = 'on_track';
  let level = ESCALATION_LEVELS.NORMAL;
  let message = 'Saken behandles innenfor tidsfristene.';

  if (isClosed) {
    return { status: 'completed', level: 0, message: 'Saken er ferdigbehandlet.', deadlineAck, deadlineFeedback, deadlineReported };
  }

  // Sjekk Bekreftelsesfrist (7 dager)
  if (!isAcked) {
    const hoursToAck = (deadlineAck - now) / (1000 * 60 * 60);
    if (now > new Date(deadlineAck.getTime() + 7 * 24 * 60 * 60 * 1000)) {
      status = 'critical_ack';
      level = ESCALATION_LEVELS.CRITICAL;
      message = 'ALVORLIG: Bekreftelse av mottak er over 7 dager forsinket.';
    } else if (now > deadlineAck) {
      status = 'overdue_ack';
      level = ESCALATION_LEVELS.OVERDUE;
      message = 'Lovpålagt frist for bekreftelse (7 dager) er utgått.';
    } else if (hoursToAck < 48) {
      status = 'urgent_ack';
      level = ESCALATION_LEVELS.WARNING;
      message = 'Frist for bekreftelse utgår om mindre enn 48 timer.';
    }
  } 
  
  // Sjekk Svarfrist fra omvarslet
  if (deadlineReported && !hasReportedReplied) {
    if (now > deadlineReported) {
      status = 'overdue_reported';
      level = Math.max(level, ESCALATION_LEVELS.OVERDUE);
      message = 'Frist for tilsvar fra omvarslet person er utgått.';
    } else {
      const daysToReported = (deadlineReported - now) / (1000 * 60 * 60 * 24);
      if (daysToReported < 3) {
        status = 'urgent_reported';
        level = Math.max(level, ESCALATION_LEVELS.WARNING);
        message = 'Frist for tilsvar fra omvarslet person utgår snart.';
      }
    }
  }

  // Sjekk Tilbakemeldingsfrist (3 mnd) - overstyrer kun hvis mer alvorlig
  const daysToFeedback = (deadlineFeedback - now) / (1000 * 60 * 60 * 24);
  if (now > new Date(deadlineFeedback.getTime() + 14 * 24 * 60 * 60 * 1000)) {
    status = 'critical_feedback';
    level = Math.max(level, ESCALATION_LEVELS.CRITICAL);
    message = 'KRITISK: Lovpålagt frist for tilbakemelding er betydelig overskredet.';
  } else if (now > deadlineFeedback) {
    status = 'overdue_feedback';
    level = Math.max(level, ESCALATION_LEVELS.OVERDUE);
    message = 'Lovpålagt frist for tilbakemelding (3 måneder) er utgått.';
  } else if (daysToFeedback < 7) {
    status = 'urgent_feedback';
    level = Math.max(level, ESCALATION_LEVELS.WARNING);
    message = 'Frist for sluttbehandling utgår om under en uke.';
  }

  return {
    status,
    level,
    message,
    deadlineAck,
    deadlineFeedback,
    deadlineReported,
    isAcked,
    isClosed,
    hasReportedReplied
  };
};

export const getStatusUI = (level) => {
  switch (level) {
    case ESCALATION_LEVELS.CRITICAL:
      return { color: 'red', icon: FiAlertCircle, label: 'KRITISK' };
    case ESCALATION_LEVELS.OVERDUE:
      return { color: 'orange', icon: FiAlertTriangle, label: 'FORSINKET' };
    case ESCALATION_LEVELS.WARNING:
      return { color: 'amber', icon: FiClock, label: 'HASTER' };
    default:
      return { color: 'green', icon: FiCheckCircle, label: 'I RUTE' };
  }
};